import { readDocumentVisibilityState } from "./documentVisibility";
import type { RecordingWireClient } from "./recordingWire";

const DEFAULT_SETTLE_MS = 5_000;
const DEFAULT_MAX_TRACKED_CONVERSATIONS = 32;

type LeavingHintSource = "pagehide" | "visibilitychange";

export interface PassiveLeavingHintTrackerOptions {
	wire: Pick<RecordingWireClient, "leavingHint"> & Partial<Pick<RecordingWireClient, "leavingHintRetract">>;
	laneLabel: string;
	suppressFor?: (conversationId: string) => boolean;
	settleMs?: number;
	maxTrackedConversations?: number;
	/**
	 * LSP-22802: when the page is visible again, retract each visibilitychange hint this lane sent during the
	 * hidden episode, so a short app switch does not leave the server's sticky 90s leaving window in place for
	 * the customer's next photos. pagehide hints are never retracted. Off by default: contact-admin's
	 * batch-drain completion hint shares the server's visibilitychange entry, and an admin tabbing away and
	 * back must not undo it.
	 */
	retractVisibilityHintOnReturn?: boolean;
}

export interface PassiveLeavingHintTracker {
	noteContentCompleted(conversationId: string | undefined): void;
	sendCompletionHint(conversationId: string | undefined): boolean;
	onVisibilityHidden(): void;
	onPageHide(event?: PageTransitionEvent): void;
	onVisible(): void;
	/** LSP-22802: whether this lane sent a visibilitychange hint for the conversation in the current hidden episode. */
	hasVisibilityHintThisEpisode(conversationId: string): boolean;
	install(): void;
	destroy(): void;
}

interface SendHintOptions {
	allowTrailingRepeat?: boolean;
	requireHidden?: boolean;
	markEpisode?: boolean;
}

export function createPassiveLeavingHintTracker(options: PassiveLeavingHintTrackerOptions): PassiveLeavingHintTracker {
	const settleMs = options.settleMs ?? DEFAULT_SETTLE_MS;
	const maxTrackedConversations = options.maxTrackedConversations ?? DEFAULT_MAX_TRACKED_CONVERSATIONS;
	const eligibleConversations = new Set<string>();
	const visibilityHintedConversations = new Set<string>();
	const pageHideHintedConversations = new Set<string>();
	const trailingTimers = new Map<string, ReturnType<typeof setTimeout>>();
	const pendingTrailingRecoveryConversations = new Set<string>();
	let installed = false;
	let destroyed = false;

	const logPrefix = `[LSP-21286] ${options.laneLabel} leaving-hint`;

	function cancelTrailingTimer(conversationId: string): void {
		const timer = trailingTimers.get(conversationId);
		if (timer === undefined) return;
		clearTimeout(timer);
		trailingTimers.delete(conversationId);
	}

	function eligibleConversationContext(): string {
		return [...eligibleConversations].join(",") || "(none)";
	}

	function browserCanSendBeacon(source: LeavingHintSource, conversationId: string | undefined): boolean {
		try {
			return typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function";
		} catch (error) {
			console.warn(
				`${logPrefix} sendBeacon capability check threw, Source=${source}, ConversationId=${conversationId ?? "(none)"}`,
				error
			);
			return false;
		}
	}

	function sendHint(conversationId: string | undefined, source: LeavingHintSource, sendOptions: SendHintOptions = {}): boolean {
		const { allowTrailingRepeat = false, requireHidden = true, markEpisode = true } = sendOptions;
		console.debug(`${logPrefix} handler fired, Source=${source}, ConversationId=${conversationId ?? "(none)"}`);
		if (!browserCanSendBeacon(source, conversationId)) {
			// LSP-21423: warn, not debug — on a teardown this is a hint that was owed and is now lost.
			console.warn(
				`${logPrefix} skipped — navigator.sendBeacon unavailable, Source=${source}, ConversationId=${conversationId ?? "(none)"}`
			);
			return false;
		}
		if (requireHidden && readDocumentVisibilityState() !== "hidden") {
			console.info(`${logPrefix} skipped — document is visible, Source=${source}, ConversationId=${conversationId ?? "(none)"}`);
			return false;
		}
		if (!conversationId) {
			console.debug(`${logPrefix} skipped — no conversationId, Source=${source}`);
			return false;
		}
		if (!eligibleConversations.has(conversationId)) {
			console.debug(`${logPrefix} skipped — no completed item for this lane, Source=${source}, ConversationId=${conversationId}`);
			return false;
		}
		const hintedConversations = source === "pagehide" ? pageHideHintedConversations : visibilityHintedConversations;
		if (hintedConversations.has(conversationId) && !allowTrailingRepeat) {
			console.debug(`${logPrefix} skipped — already hinted this episode, Source=${source}, ConversationId=${conversationId}`);
			return false;
		}
		// Cross-lane suppression deduplicates the initial teardown only. A completion after
		// that teardown may have re-widened the server timer, so its settled repeat must send.
		if (options.suppressFor && !allowTrailingRepeat) {
			let suppressed = false;
			try {
				suppressed = options.suppressFor(conversationId);
			} catch (error) {
				console.warn(`${logPrefix} suppression check threw, Source=${source}, ConversationId=${conversationId}`, error);
			}
			if (suppressed) {
				console.debug(
					`${logPrefix} skipped — another lane covers this conversation, Source=${source}, ConversationId=${conversationId}`
				);
				return false;
			}
		}

		try {
			const sent = options.wire.leavingHint({ conversationId, source });
			if (!sent) {
				console.warn(`${logPrefix} beacon refused, Source=${source}, ConversationId=${conversationId}`);
				return false;
			}
			if (markEpisode) hintedConversations.add(conversationId);
			return true;
		} catch (error) {
			console.warn(`${logPrefix} sender threw, Source=${source}, ConversationId=${conversationId}`, error);
			return false;
		}
	}

	function sendCompletionHint(conversationId: string | undefined): boolean {
		console.debug(`${logPrefix} completion hint requested, ConversationId=${conversationId ?? "(none)"}`);
		if (destroyed) {
			console.debug(`${logPrefix} completion hint skipped — tracker destroyed, ConversationId=${conversationId ?? "(none)"}`);
			return false;
		}
		return sendHint(conversationId, "visibilitychange", {
			allowTrailingRepeat: true,
			requireHidden: false,
			markEpisode: false
		});
	}

	function armTrailingHint(conversationId: string): void {
		cancelTrailingTimer(conversationId);
		const timer = setTimeout(() => {
			trailingTimers.delete(conversationId);
			if (destroyed) {
				console.debug(`${logPrefix} trailing timer skipped — tracker destroyed, ConversationId=${conversationId}`);
				return;
			}
			if (readDocumentVisibilityState() !== "hidden") {
				console.debug(`${logPrefix} trailing timer skipped — document became visible, ConversationId=${conversationId}`);
				return;
			}
			try {
				// A completion that occurs after the initial hidden hint needs a fresh signal after
				// its server-side finalizer has had time to run, even within the same hidden episode.
				if (sendHint(conversationId, "visibilitychange", { allowTrailingRepeat: true })) {
					pendingTrailingRecoveryConversations.delete(conversationId);
				} else {
					// Deliberately no autonomous or cross-conversation retry: this stays eligible only
					// for its own next teardown. If none occurs, the hint can remain unsent for this
					// hidden episode; that is an accepted limitation of the conservative retry model.
					pendingTrailingRecoveryConversations.add(conversationId);
					console.debug(`${logPrefix} trailing hint remains pending for the next teardown, ConversationId=${conversationId}`);
				}
			} catch (error) {
				pendingTrailingRecoveryConversations.add(conversationId);
				console.warn(`${logPrefix} trailing handler threw, ConversationId=${conversationId}`, error);
			}
		}, settleMs);
		trailingTimers.set(conversationId, timer);
	}

	function noteContentCompleted(conversationId: string | undefined): void {
		if (destroyed) {
			console.debug(`${logPrefix} completion skipped — tracker destroyed, ConversationId=${conversationId ?? "(none)"}`);
			return;
		}
		if (!conversationId) {
			console.debug(`${logPrefix} completion skipped — no conversationId, ConversationId=(none)`);
			return;
		}
		if (!eligibleConversations.has(conversationId) && eligibleConversations.size >= maxTrackedConversations) {
			// Deliberately evict oldest-first: an entry with no timer may still be waiting for its first
			// teardown, so timer/recovery state is not a safe proxy for eviction priority. Under capacity
			// pressure, the oldest pending recovery can therefore be evicted before another teardown; that
			// is an accepted limitation of the bounded 32-conversation tracker.
			const oldestConversationId = eligibleConversations.values().next().value as string | undefined;
			const evictionConversationId = oldestConversationId;
			if (evictionConversationId) {
				const evictsArmedTrailingHint = trailingTimers.has(evictionConversationId);
				eligibleConversations.delete(evictionConversationId);
				visibilityHintedConversations.delete(evictionConversationId);
				pageHideHintedConversations.delete(evictionConversationId);
				pendingTrailingRecoveryConversations.delete(evictionConversationId);
				cancelTrailingTimer(evictionConversationId);
				if (evictsArmedTrailingHint) {
					console.warn(
						`${logPrefix} evicted conversation with armed trailing hint at capacity, ConversationId=${evictionConversationId}`
					);
				} else {
					console.debug(`${logPrefix} evicted oldest eligible conversation, ConversationId=${evictionConversationId}`);
				}
			}
		}
		eligibleConversations.add(conversationId);
		if (readDocumentVisibilityState() === "hidden") armTrailingHint(conversationId);
	}

	function onVisibilityHidden(): void {
		if (destroyed) {
			console.debug(
				`${logPrefix} skipped — tracker destroyed, Source=visibilitychange, ConversationId=${eligibleConversationContext()}`
			);
			return;
		}
		if (eligibleConversations.size === 0) {
			console.debug(`${logPrefix} skipped — no completed item for this lane, Source=visibilitychange, ConversationId=(none)`);
			return;
		}
		for (const conversationId of eligibleConversations) {
			try {
				const retriesPendingTrailingHint = pendingTrailingRecoveryConversations.has(conversationId);
				if (sendHint(conversationId, "visibilitychange", { allowTrailingRepeat: retriesPendingTrailingHint })) {
					pendingTrailingRecoveryConversations.delete(conversationId);
					cancelTrailingTimer(conversationId);
				}
			} catch (error) {
				console.warn(`${logPrefix} visibilitychange listener callee threw, ConversationId=${conversationId}`, error);
			}
		}
	}

	function onPageHide(event?: PageTransitionEvent): void {
		if (destroyed) {
			console.debug(`${logPrefix} skipped — tracker destroyed, Source=pagehide, ConversationId=${eligibleConversationContext()}`);
			return;
		}
		if (event?.persisted === true) {
			console.debug(`${logPrefix} skipped — pagehide persisted for bfcache, ConversationId=${eligibleConversationContext()}`);
			return;
		}
		if (eligibleConversations.size === 0) {
			console.debug(`${logPrefix} skipped — no completed item for this lane, Source=pagehide, ConversationId=(none)`);
			return;
		}
		// LSP-21423: the HTML unload steps fire `pagehide` BEFORE they set visibilityState to "hidden", so
		// a genuine teardown whose `visibilitychange:hidden` did not come first still reads "visible"
		// here. The only non-leaving pagehide is a bfcache freeze, already excluded by `persisted` above,
		// so this source must not require "hidden". Per-source latches still keep it once per episode.
		const visibilityState = readDocumentVisibilityState();
		if (visibilityState !== "hidden") {
			console.info(
				`${logPrefix} pagehide fired before visibilitychange:hidden, sending without the hidden gate, VisibilityState=${visibilityState}, ConversationId=${eligibleConversationContext()}`
			);
		}
		for (const conversationId of eligibleConversations) {
			try {
				const retriesPendingTrailingHint = pendingTrailingRecoveryConversations.has(conversationId);
				if (
					sendHint(conversationId, "pagehide", { allowTrailingRepeat: retriesPendingTrailingHint, requireHidden: false }) &&
					retriesPendingTrailingHint
				) {
					pendingTrailingRecoveryConversations.delete(conversationId);
					cancelTrailingTimer(conversationId);
				}
			} catch (error) {
				console.warn(`${logPrefix} pagehide listener callee threw, ConversationId=${conversationId}`, error);
			}
		}
	}

	function retractVisibilityHint(conversationId: string): void {
		try {
			if (!options.wire.leavingHintRetract) {
				console.debug(`${logPrefix} retraction skipped — wire cannot retract, ConversationId=${conversationId}`);
				return;
			}
			const sent = options.wire.leavingHintRetract({ conversationId });
			if (sent) console.info(`${logPrefix} retracted visibilitychange hint — page visible again, ConversationId=${conversationId}`);
			// A lost retraction leaves the server's short sticky wait in place (the pre-LSP-22802 behavior).
			else console.warn(`${logPrefix} retraction beacon refused, ConversationId=${conversationId}`);
		} catch (error) {
			console.warn(`${logPrefix} retraction threw, ConversationId=${conversationId}`, error);
		}
	}

	function onVisible(): void {
		if (destroyed) {
			console.debug(`${logPrefix} visible reset skipped — tracker destroyed, ConversationId=${eligibleConversationContext()}`);
			return;
		}
		if (options.retractVisibilityHintOnReturn) {
			for (const conversationId of visibilityHintedConversations) retractVisibilityHint(conversationId);
		}
		visibilityHintedConversations.clear();
		pageHideHintedConversations.clear();
		pendingTrailingRecoveryConversations.clear();
		for (const conversationId of trailingTimers.keys()) cancelTrailingTimer(conversationId);
	}

	const visibilityListener = (): void => {
		try {
			if (readDocumentVisibilityState() === "hidden") onVisibilityHidden();
			else onVisible();
		} catch (error) {
			console.warn(`${logPrefix} visibilitychange listener threw, ConversationId=${eligibleConversationContext()}`, error);
		}
	};
	const pageHideListener = (event: PageTransitionEvent): void => {
		try {
			onPageHide(event);
		} catch (error) {
			console.warn(`${logPrefix} pagehide listener threw, ConversationId=${eligibleConversationContext()}`, error);
		}
	};
	const pageShowListener = (): void => {
		try {
			onVisible();
		} catch (error) {
			console.warn(`${logPrefix} pageshow listener threw, ConversationId=${eligibleConversationContext()}`, error);
		}
	};

	function install(): void {
		if (installed || destroyed || typeof window === "undefined" || typeof document === "undefined") return;
		let visibilityInstalled = false;
		let pageHideInstalled = false;
		try {
			document.addEventListener("visibilitychange", visibilityListener);
			visibilityInstalled = true;
			window.addEventListener("pagehide", pageHideListener);
			pageHideInstalled = true;
			window.addEventListener("pageshow", pageShowListener);
			installed = true;
		} catch (error) {
			if (visibilityInstalled) document.removeEventListener("visibilitychange", visibilityListener);
			if (pageHideInstalled) window.removeEventListener("pagehide", pageHideListener);
			console.warn(`${logPrefix} listener installation threw, ConversationId=${eligibleConversationContext()}`, error);
			throw error;
		}
	}

	function destroy(): void {
		if (destroyed) return;
		destroyed = true;
		if (installed && typeof window !== "undefined" && typeof document !== "undefined") {
			document.removeEventListener("visibilitychange", visibilityListener);
			window.removeEventListener("pagehide", pageHideListener);
			window.removeEventListener("pageshow", pageShowListener);
		}
		installed = false;
		pendingTrailingRecoveryConversations.clear();
		for (const conversationId of trailingTimers.keys()) cancelTrailingTimer(conversationId);
	}

	function hasVisibilityHintThisEpisode(conversationId: string): boolean {
		return visibilityHintedConversations.has(conversationId);
	}

	return {
		noteContentCompleted,
		sendCompletionHint,
		onVisibilityHidden,
		onPageHide,
		onVisible,
		hasVisibilityHintThisEpisode,
		install,
		destroy
	};
}
